import { ComponentProps, useMemo } from 'react'

import { FatigueModel, FatigueModelChangeStateEnum } from '@/entities/schedules/models/FatigueModel'
import { Icon } from '@/shared/components'
import { faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import { Box, Button, FormHelperText, styled } from '@mui/material'
import { GridColDef, GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-premium'

import { TimeEditCell } from '../GridTable/GridCells/TimeEditCell'
import { GridTable } from '../GridTable/GridTable'
import { GridRowActionItem } from '../GridTable/models/GridRowActions'

export type BreakConfigurationFieldProps = Pick<ComponentProps<typeof StyledContainer>, 'sx'> & {
  value: FatigueModel[]
  disabled?: boolean
  error?: boolean
  errorMessage?: string
  isCustomBreaks?: boolean
  onChange: (value: FatigueModel[]) => void
}

export const BreakConfigurationField = ({
  value,
  error,
  errorMessage,
  disabled,
  isCustomBreaks = false,
  onChange,
  ...props
}: BreakConfigurationFieldProps) => {
  const apiRef = useGridApiRef()
  const rows = useMemo(() => {
    if (!value) return []

    return value.map((row) => ({
      ...row,
      isSelectable: !disabled,
      isEditable: !disabled,
      hasHotAction: !disabled,
    })).filter(row => row.state !== FatigueModelChangeStateEnum.Deleted)
  }, [value, disabled])

  const columns: GridColDef[] = [
    {
      field: 'numberOfBreaks',
      headerName: 'Number of Breaks',
      description: 'Number of Breaks the Driver must take in the specified Time Period',
      width: 150,
      align: 'left',
      headerAlign: 'left',
      editable: !disabled && isCustomBreaks,
      type: 'number',
    },
    {
      field: 'timeInterval',
      headerName: 'Time Period',
      description: 'Driving Time Period of the Run',
      width: 150,
      align: 'left',
      headerAlign: 'left',
      editable: !disabled && isCustomBreaks,
      renderEditCell: (params) => (
        <TimeEditCell
          {...params}
          placeholder="HH:mm"
          format="HH:mm"
        />
      ),
    },
  ]

  const handleAddRow = () => {
    const newId = value.length + 1
    const newRow: FatigueModel = {
      id: newId,
      numberOfBreaks: 0,
      timeInterval: '00:00',
      state: FatigueModelChangeStateEnum.Added,
    }

    onChange([...value, newRow])
    setTimeout(() => {
      if (apiRef.current) {
        apiRef.current.scrollToIndexes({ rowIndex: rows.length })
        apiRef.current.startCellEditMode({ id: newId, field: 'numberOfBreaks' })
      }
    }, 100)
  }

  const handleDeleteRow = (id: number) => {
    if (isCustomBreaks) {
      const updatedModels = value.map(row => {
        if (row.id === id) {
          return {
            ...row,
            state: FatigueModelChangeStateEnum.Deleted,
          }
        }

        return row
      })

      onChange(updatedModels)
    }
  }

  const deleteBreakAction: GridRowActionItem = {
    icon: faTrashCan,
    label: 'Delete Row',
    onClick: (rowData: GridValidRowModel) => {
      handleDeleteRow(rowData.id)
    },
    disabled: disabled || !isCustomBreaks,
  }

  return (
    <StyledContainer {...props}>
      <StyledGridTable
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        hideFooter
        processRowUpdate={(newRow) => {
          const updatedModels = value.map(row => {
            if (row.id === newRow.id) {
              return {
                ...row,
                ...newRow,
                state: row.state === FatigueModelChangeStateEnum.Added ? FatigueModelChangeStateEnum.Added : FatigueModelChangeStateEnum.Modified,
              }
            }

            return row
          })

          onChange(updatedModels)

          return newRow
        }}
        slots={{
          toolbar: () => (
            isCustomBreaks && !disabled ? (
              <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end', marginRight: '-10px' }}>
                <Button
                  onClick={handleAddRow}
                  startIcon={<Icon icon={faPlus} />}
                >
                  Add Row
                </Button>
              </Box>
            ) : null
          ),
        }}
        hotAction={deleteBreakAction}
      />
      <StyledHelperText error={error}>
        {error ? (errorMessage || 'Please ensure all required fields are filled') : (!disabled ? 'Double-click cell to update value' : '')}
      </StyledHelperText>
    </StyledContainer>
  )
}

const StyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledHelperText = styled(FormHelperText)`
  margin-bottom: ${(props) => props.theme.typography.pxToRem(8)};
`

const StyledGridTable = styled(GridTable)`
  min-height: ${(props) => props.theme.typography.pxToRem(180)};
`